// 092 -- Admin product catalog editor. Only meaningful for order_email providers.

import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import type { ProductPublic, ProviderPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";

interface ProductFormState {
  sku: string;
  name: string;
  description: string;
  image_url: string;
  price_rand: string; // stored as Rand string for UX; converted to cents on submit
  // 094 -- per-buyer discount pcts as strings for UX (empty = null on the wire).
  discount_pct_parent: string;
  discount_pct_school: string;
  active: boolean;
  sort_order: number;
}

const EMPTY_PRODUCT: ProductFormState = {
  sku: "",
  name: "",
  description: "",
  image_url: "",
  price_rand: "",
  discount_pct_parent: "",
  discount_pct_school: "",
  active: true,
  sort_order: 0,
};

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

export default function AdminShopProductsPage() {
  const { id } = useParams<{ id: string }>();
  const [provider, setProvider] = useState<ProviderPublic | null>(null);
  const [products, setProducts] = useState<ProductPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProductFormState>(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const adminR = await api.getAdminProviders();
      const p = adminR.providers.find((x) => x.id === id || x.slug === id);
      if (!p) throw new Error("Provider not found");
      setProvider(p);
      if (p.fulfillment_type === "order_email") {
        // Reuse the public endpoint for the product list -- it already returns full catalog.
        const detail = await api.getShopProvider(p.slug);
        setProducts(detail.products);
      } else {
        setProducts([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function startEdit(p: ProductPublic) {
    setEditingId(p.id);
    setForm({
      sku: p.sku,
      name: p.name,
      description: p.description ?? "",
      image_url: p.image_url ?? "",
      price_rand: (p.price_cents / 100).toFixed(2),
      discount_pct_parent: p.discount_pct_parent != null ? String(p.discount_pct_parent) : "",
      discount_pct_school: p.discount_pct_school != null ? String(p.discount_pct_school) : "",
      active: p.active,
      sort_order: p.sort_order,
    });
    setShowForm(true);
  }

  function startNew() {
    setEditingId(null);
    setForm(EMPTY_PRODUCT);
    setShowForm(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!provider) return;
    setSaving(true);
    setError(null);
    try {
      const priceRand = parseFloat(form.price_rand);
      if (Number.isNaN(priceRand) || priceRand < 0) {
        throw new Error("Price must be a positive number");
      }
      const parsePct = (s: string): number | null => {
        if (s.trim() === "") return null;
        const n = Math.round(Number(s));
        if (Number.isNaN(n) || n < 0 || n > 100) throw new Error("Discount must be a whole number between 0 and 100");
        return n;
      };
      const payload = {
        sku: form.sku,
        name: form.name,
        description: form.description,
        image_url: form.image_url,
        price_cents: Math.round(priceRand * 100),
        discount_pct_parent: parsePct(form.discount_pct_parent),
        discount_pct_school: parsePct(form.discount_pct_school),
        active: form.active,
        sort_order: form.sort_order,
      };
      if (editingId) {
        await api.updateAdminProduct(editingId, payload);
      } else {
        await api.createAdminProduct(provider.id, payload);
      }
      setShowForm(false);
      setForm(EMPTY_PRODUCT);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(productId: string, name: string) {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      await api.deleteAdminProduct(productId);
      await load();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to delete");
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="admin-shop-products" data-state="loading">Loading…</p>;
  if (!provider) return (
    <div data-page="admin-shop-products" data-state="error" className="flex flex-col gap-4">
      <Breadcrumbs items={[{ label: "Admin" }, { label: "Shop providers", to: "/admin/shop/providers" }]} />
      <p className="text-sm text-destructive" data-error>{error ?? "Provider not found"}</p>
    </div>
  );

  if (provider.fulfillment_type !== "order_email") {
    return (
      <div data-page="admin-shop-products" data-state="unsupported" className="flex flex-col gap-4">
        <Breadcrumbs
          items={[
            { label: "Admin" },
            { label: "Shop providers", to: "/admin/shop/providers" },
            { label: provider.name, to: `/admin/shop/providers/${provider.id}/edit` },
            { label: "Products" },
          ]}
        />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Products aren't managed here -- {provider.name} is an external-redirect provider.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-page="admin-shop-products" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: "Admin" },
          { label: "Shop providers", to: "/admin/shop/providers" },
          { label: provider.name, to: `/admin/shop/providers/${provider.id}/edit` },
          { label: "Products" },
        ]}
      />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold">{provider.name} -- products</h1>
        <Button onClick={startNew} size="sm" data-action="new-product">+ New product</Button>
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {showForm && (
        <Card data-region="product-form">
          <CardHeader><CardTitle className="text-base">{editingId ? "Edit product" : "New product"}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid gap-4 sm:grid-cols-2" data-form="product">
              <div className="flex flex-col gap-2">
                <Label htmlFor="p-sku">SKU</Label>
                <Input id="p-sku" data-field="sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} required />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="p-name">Name</Label>
                <Input id="p-name" data-field="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="p-price">Price (ZAR)</Label>
                <Input id="p-price" data-field="price_rand" type="number" step="0.01" min="0" value={form.price_rand} onChange={(e) => setForm({ ...form, price_rand: e.target.value })} required />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="p-sort">Sort order</Label>
                <Input id="p-sort" data-field="sort_order" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })} />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="p-disc-parent">Parent discount % <span className="text-xs text-muted-foreground">(optional, 0-100)</span></Label>
                <Input
                  id="p-disc-parent"
                  data-field="discount_pct_parent"
                  type="number"
                  min="0"
                  max="100"
                  value={form.discount_pct_parent}
                  onChange={(e) => setForm({ ...form, discount_pct_parent: e.target.value })}
                  placeholder="e.g. 10"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="p-disc-school">School discount % <span className="text-xs text-muted-foreground">(optional, 0-100)</span></Label>
                <Input
                  id="p-disc-school"
                  data-field="discount_pct_school"
                  type="number"
                  min="0"
                  max="100"
                  value={form.discount_pct_school}
                  onChange={(e) => setForm({ ...form, discount_pct_school: e.target.value })}
                  placeholder="e.g. 25"
                />
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <Label htmlFor="p-image">Image URL</Label>
                <Input id="p-image" data-field="image_url" type="url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <Label htmlFor="p-desc">Description</Label>
                <textarea
                  id="p-desc"
                  data-field="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  aria-label="Description"
                  placeholder="Short description shown on the product tile"
                />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <input
                  id="p-active"
                  data-field="active"
                  type="checkbox"
                  aria-label="Active"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                <Label htmlFor="p-active">Active</Label>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <Button type="submit" disabled={saving} data-action="save">
                  {saving ? "Saving…" : editingId ? "Save changes" : "Add product"}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_PRODUCT); }} data-action="cancel">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {products.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No products yet. <Link to="#" onClick={(e) => { e.preventDefault(); startNew(); }} className="text-primary hover:underline">Add the first one</Link>.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-region="products">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 px-4 text-left">Name</th>
                  <th className="py-2 px-4 text-left">SKU</th>
                  <th className="py-2 px-4 text-right">Price</th>
                  <th className="py-2 px-4 text-center">Active</th>
                  <th className="py-2 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-border/60" data-item="product" data-item-id={p.id}>
                    <td className="py-3 px-4">
                      <p className="font-medium">{p.name}</p>
                      {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{p.sku}</td>
                    <td className="py-3 px-4 text-right tabular-nums">{fmtMoney(p.price_cents, p.currency)}</td>
                    <td className="py-3 px-4 text-center">
                      {p.active ? (
                        <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs text-green-800">Active</span>
                      ) : (
                        <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">Hidden</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button variant="outline" size="sm" onClick={() => startEdit(p)} data-action="edit">Edit</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(p.id, p.name)}
                        data-action="delete"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
